import type { ChartData, ChartDatapoint } from '../../core/types/chart-data.js';
import type {
  SankeyGraphLink,
  SankeyGraphModel,
  SankeyGraphNode,
} from '../types/sankey-internal.js';

interface BuildSankeyGraphParams {
  data: ChartData;
  sourceAccessor: string;
  targetAccessor: string;
  valueAccessor: string;
  nodes?: ChartData;
  nodeIdAccessor?: string;
  nodeLabelAccessor?: string;
  columnAccessor?: string;
}

const resolveString = (
  row: ChartDatapoint,
  accessor: string | undefined,
): string | undefined => {
  if (!accessor) {
    return undefined;
  }
  const value = row[accessor];
  return value === null || value === undefined ? undefined : String(value);
};

const resolveGraphNode = (
  id: string,
  node: ChartDatapoint | undefined,
  nodeLabelAccessor: string | undefined,
  columnAccessor: string | undefined,
): SankeyGraphNode => {
  if (!node) {
    return { id, label: id };
  }
  const columnValue = resolveString(node, columnAccessor);
  return {
    ...node,
    id,
    label: resolveString(node, nodeLabelAccessor) ?? id,
    column: columnValue === undefined ? undefined : Number(columnValue),
  };
};

/**
 * Resolves the link rows and optional node metadata into the domain graph
 * model. Link endpoints are collected in a single pass over `data`; node
 * metadata is only indexed from `nodes` afterwards, so the (typically much
 * smaller) `nodes` array is never re-scanned per link.
 */
export const buildSankeyGraph = ({
  data,
  sourceAccessor,
  targetAccessor,
  valueAccessor,
  nodes,
  nodeIdAccessor,
  nodeLabelAccessor,
  columnAccessor,
}: BuildSankeyGraphParams): SankeyGraphModel => {
  const links: SankeyGraphLink[] = [];
  const nodesById = new Map<string, SankeyGraphNode>();

  for (const link of data) {
    const source = String(link[sourceAccessor]);
    const target = String(link[targetAccessor]);
    const value = Number(link[valueAccessor]);
    links.push({ ...link, source, target, value });

    nodesById.set(source, { id: source, label: source });
    nodesById.set(target, { id: target, label: target });
  }

  nodes?.forEach((node) => {
    const id = String(node[nodeIdAccessor ?? 'id']);
    nodesById.set(
      id,
      resolveGraphNode(id, node, nodeLabelAccessor, columnAccessor),
    );
  });

  return { nodes: Array.from(nodesById.values()), links };
};
